/**
 * feat-300 — the inline thread panel under a question card (design NOTES §3.3,
 * §3.7, §5; ADR §10.4–§10.6).
 *
 * Header (`AI edit · {state}` + Hide), then the log, then the composer. Owns
 * the draft text, the recorder, the mutations' error handling and every focus
 * move in NOTES §5.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import { QuizQuestion } from '../../../../services/quizContentAdminService';
import {
  isOpenThread,
  questionEditErrorCode,
  questionEditErrorThread,
  QuestionEditThread,
} from '../../../../services/questionEditService';
import { ItemPendingChip } from '../reviewEdit';
import { useQuestionEdit } from './QuestionEditProvider';
import { focusTrigger, panelDomId } from './MicButton';
import Composer from './Composer';
import ThreadLog from './ThreadLog';
import { ApplyOutcome } from './ProposalCard';
import { useRecorder } from './useRecorder';
import { useEditCoordinator } from '../editCoordinator';
import { HISTORY_ATTR } from './HistoryLines';
import {
  BOX_CLOSED,
  BTN_QUIET,
  LOG,
  NOTE_DANGER,
  NOTE_INFO,
  PANEL,
  PANEL_HEAD,
  PANEL_STATE,
  PANEL_TITLE,
} from './classes';
import { QUESTION_EDIT_COPY as COPY } from './voiceEditCopy';

type Notice =
  | { kind: 'info'; text: string }
  | { kind: 'danger'; text: string }
  | { kind: 'closed'; text: string };

function stateWord(thread: QuestionEditThread | null): string {
  if (!thread) return COPY.panelState.notSent;
  switch (thread.state) {
    case 'working':
      return COPY.panelState.working;
    case 'needs_reply':
      return COPY.panelState.needsReply;
    case 'proposal_ready':
      return COPY.panelState.proposal;
    case 'failed':
      return COPY.panelState.failed;
    case 'applied':
      return COPY.panelState.applied;
    case 'discarded':
      return COPY.panelState.discarded;
    default:
      return '';
  }
}

const ThreadPanel: React.FC<{ q: QuizQuestion; index: number }> = ({ q, index }) => {
  const ctx = useQuestionEdit()!;
  const thread = ctx.panelThreadFor(q.id);
  const open = thread && isOpenThread(thread) ? thread : null;
  const panelId = panelDomId(q.id);
  const h3Id = `${panelId}-title`;

  const h3Ref = useRef<HTMLHeadingElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const stopRef = useRef<HTMLButtonElement>(null);
  const closedBoxRef = useRef<HTMLDivElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);

  const [draft, setDraft] = useState('');
  const [voiceUsed, setVoiceUsed] = useState(false);
  const [fromRecording, setFromRecording] = useState(false);
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  const focusTextarea = useCallback((caretToEnd = false) => {
    window.setTimeout(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      if (caretToEnd) {
        const n = el.value.length;
        el.setSelectionRange?.(n, n);
      }
    }, 0);
  }, []);

  // --- the recorder ------------------------------------------------------------
  const onTranscript = useCallback(
    (text: string) => {
      // Replace an empty box; otherwise append after a space (NOTES §3.5).
      setDraft((prev) => (prev.trim() === '' ? text : `${prev.replace(/\s+$/, '')} ${text}`));
      setVoiceUsed(true);
      setFromRecording(true);
      focusTextarea(true);
    },
    [focusTextarea],
  );
  const rawRecorder = useRecorder(q.id, onTranscript);
  // R2 3d: starting a recording commits any open feat-299 inline editor first,
  // through its coordinator — never by fighting its focus.
  const coordinator = useEditCoordinator();
  const rawStart = rawRecorder.start;
  const startWithCommit = useCallback(() => {
    coordinator?.commitOpen();
    return rawStart();
  }, [coordinator, rawStart]);
  const recorder = useMemo(
    () => ({ ...rawRecorder, start: startWithCommit }),
    [rawRecorder, startWithCommit],
  );

  // Recording started → focus Stop (NOTES §5).
  useEffect(() => {
    if (recorder.phase === 'recording') stopRef.current?.focus();
  }, [recorder.phase]);
  // Mic denied / unavailable → the textarea, so typing is one keystroke away.
  useEffect(() => {
    if (recorder.problem === 'denied' || recorder.problem === 'unavailable') focusTextarea();
  }, [recorder.problem, focusTextarea]);

  // --- intents and focus requests (Ask AI, Type, chip, counter jump) -------------
  const { focusRequest, requestFocus, takeIntent } = ctx;
  const startRecording = recorder.start;
  useEffect(() => {
    if (focusRequest !== q.id) return;
    requestFocus(null);
    const intent = takeIntent(q.id);
    if (intent === 'voice') {
      startRecording();
    } else if (intent === 'type') {
      focusTextarea();
    } else {
      h3Ref.current?.focus({ preventScroll: true });
    }
  }, [focusRequest, requestFocus, takeIntent, q.id, startRecording, focusTextarea]);

  // --- a thread that just closed: focus its box and announce (NOTES §5) -------
  const prevStateRef = useRef(thread?.state);
  useEffect(() => {
    const prev = prevStateRef.current;
    prevStateRef.current = thread?.state;
    if (!thread || prev === thread.state) return;
    if (thread.state === 'applied' || thread.state === 'discarded') {
      window.setTimeout(() => closedBoxRef.current?.focus(), 0);
      ctx.announce(thread.state === 'applied' ? COPY.liveApplied : COPY.liveDiscarded);
    }
  }, [thread, ctx]);

  const hide = () => {
    if (recorder.phase !== 'idle') recorder.cancel();
    ctx.setPanelOpen(q.id, false);
    if (thread && (thread.state === 'applied' || thread.state === 'discarded')) {
      // After Hide, a just-closed thread is the new history line: focus it.
      const id = thread.id;
      window.setTimeout(() => {
        const nodes = document.querySelectorAll<HTMLElement>(`[${HISTORY_ATTR}]`);
        for (let i = 0; i < nodes.length; i += 1) {
          if (nodes[i].getAttribute(HISTORY_ATTR) === id) {
            nodes[i].focus();
            return;
          }
        }
        focusTrigger(q.id);
      }, 0);
      return;
    }
    focusTrigger(q.id);
  };

  // --- error handling shared by every mutation ---------------------------------
  const handleError = useCallback(
    async (e: unknown) => {
      const code = questionEditErrorCode(e);
      const t = questionEditErrorThread(e);
      if (t) ctx.mergeThread(t);
      switch (code) {
        case 'thread_open':
          // Someone else started one: show it, keep the text in the box.
          await ctx.refresh();
          return;
        case 'turn_in_progress':
          setNotice({ kind: 'info', text: COPY.conflict.turnInProgress });
          return;
        case 'thread_closed':
          if (t) ctx.stickThread(q.id, t.id);
          setNotice({ kind: 'closed', text: COPY.conflict.threadClosed });
          return;
        case 'question_deleted':
          if (t) ctx.stickThread(q.id, t.id);
          setNotice({ kind: 'closed', text: COPY.conflict.questionDeleted });
          return;
        case 'not_ready':
        case 'no_change':
        case 'not_failed':
          // Never reachable from the UI: refetch silently.
          await ctx.refresh();
          return;
        default:
          setNotice({ kind: 'danger', text: COPY.actionFailed });
      }
    },
    [ctx, q.id],
  );

  // --- send / reply ---------------------------------------------------------------
  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setNotice(null);
    const mode = voiceUsed ? 'voice' : 'typed';
    try {
      if (open) await ctx.reply(open.id, text, mode);
      else await ctx.start(q.id, text, mode);
      setDraft('');
      setVoiceUsed(false);
      setFromRecording(false);
      recorder.clearProblem();
      ctx.announce(COPY.liveSent);
      window.setTimeout(() => h3Ref.current?.focus({ preventScroll: true }), 0);
    } catch (e) {
      await handleError(e);
    } finally {
      setSending(false);
    }
  }, [draft, sending, voiceUsed, open, ctx, q.id, recorder, handleError]);

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true);
      setNotice(null);
      try {
        await fn();
      } catch (e) {
        await handleError(e);
      } finally {
        setBusy(false);
      }
    },
    [handleError],
  );

  const onApply = useCallback(async (): Promise<ApplyOutcome> => {
    if (!open) return 'error';
    setNotice(null);
    try {
      await ctx.apply(open.id);
      return 'applied';
    } catch (e) {
      if (questionEditErrorCode(e) === 'stale_base') {
        const t = questionEditErrorThread(e);
        if (t) ctx.mergeThread(t);
        // Confirm → 409: focus the stale box (NOTES §5). Done here, after the
        // merge's commit, so it holds even if the proposal card re-mounted.
        window.setTimeout(() => {
          sectionRef.current?.querySelector<HTMLElement>('[data-qe-stale]')?.focus();
        }, 0);
        return 'stale';
      }
      await handleError(e);
      return 'error';
    }
  }, [open, ctx, handleError]);

  const onCancelRecording = () => {
    recorder.cancel();
    if (open || draft.trim() !== '') {
      focusTextarea();
    } else {
      ctx.setPanelOpen(q.id, false);
      focusTrigger(q.id);
    }
  };

  // --- what to show ------------------------------------------------------------------
  const recording = recorder.phase === 'recording' || recorder.phase === 'starting';
  const word = recording
    ? COPY.panelState.recording
    : recorder.phase === 'transcribing'
      ? COPY.panelState.transcribing
      : stateWord(thread);

  const showComposer =
    !thread ||
    (open !== null &&
      (open.state === 'needs_reply' || open.state === 'failed' ||
        (open.state === 'proposal_ready' && !open.is_stale)));

  const placement = ctx.placementOf(q.id);
  const n = placement?.index ?? index;

  const closedBox =
    thread && thread.state === 'applied' ? (
      <div ref={closedBoxRef} className={BOX_CLOSED} role="status" tabIndex={-1} data-testid="qe-closed">
        <Check className="w-4 h-4 text-success-on" aria-hidden />
        <span>
          <strong className="font-semibold">{COPY.appliedLead}</strong>{' '}
          {ctx.approvalRequired === false && COPY.appliedOff}
        </span>
        {/* Approval required: never assume pending (a no-op Apply is not). The
            chip is feat-299's, from the Lesson re-read the Apply triggered, so it
            appears only when the server says this question is waiting (R2-F2). */}
        {ctx.approvalRequired !== false && <ItemPendingChip entity="question_item" id={q.id} />}
      </div>
    ) : thread && thread.state === 'discarded' ? (
      <div ref={closedBoxRef} className={BOX_CLOSED} role="status" tabIndex={-1} data-testid="qe-closed">
        <X className="w-4 h-4 text-inkMuted" aria-hidden />
        <span>
          <strong className="font-semibold">{COPY.discardedLead}</strong> {COPY.discardedTail}
        </span>
      </div>
    ) : null;

  return (
    <section
      ref={sectionRef}
      id={panelId}
      aria-labelledby={h3Id}
      className={PANEL}
      data-testid={`qe-panel-${q.id}`}
      // R2 3d: focus arriving anywhere in the panel commits an open inline editor.
      onFocusCapture={() => coordinator?.commitOpen()}
    >
      <div className={PANEL_HEAD}>
        <h3 id={h3Id} ref={h3Ref} tabIndex={-1} className={PANEL_TITLE}>
          {COPY.panelTitle}
        </h3>
        {word && (
          <span className={PANEL_STATE} aria-hidden>
            · {word}
          </span>
        )}
        <button type="button" className={`${BTN_QUIET} ml-auto`} onClick={hide}>
          {COPY.hide}
        </button>
      </div>

      {thread && (
        <div className={LOG} role="log" aria-live="polite" aria-label={COPY.logLabel(n)}>
          <ThreadLog
            thread={thread}
            q={q}
            currentEmail={ctx.currentEmail}
            approvalRequired={ctx.approvalRequired}
            actions={
              open
                ? {
                    onApply,
                    onReply: () => focusTextarea(),
                    onDiscard: () => run(() => ctx.discard(open.id)),
                    onRedo: () => run(() => ctx.reply(open.id, COPY.redoTurn, 'typed')),
                    onRetry: () => run(() => ctx.retry(open.id)),
                    busy,
                  }
                : undefined
            }
          />
          {closedBox}
          {notice?.kind === 'closed' && (
            <div className={BOX_CLOSED} role="status" data-testid="qe-conflict">
              {notice.text}
            </div>
          )}
        </div>
      )}

      {showComposer && (
        <Composer
          panelId={panelId}
          firstTurn={!open}
          text={draft}
          onTextChange={(t) => {
            setDraft(t);
            setFromRecording(false);
          }}
          fromRecording={fromRecording}
          recorder={recorder}
          onSend={send}
          sending={sending}
          textareaRef={textareaRef}
          stopRef={stopRef}
          onCancelRecording={onCancelRecording}
          notice={
            notice && notice.kind !== 'closed' ? (
              <div
                className={notice.kind === 'danger' ? NOTE_DANGER : NOTE_INFO}
                role={notice.kind === 'danger' ? 'alert' : 'status'}
                data-testid="qe-notice"
              >
                <p>{notice.text}</p>
              </div>
            ) : undefined
          }
        />
      )}
      {!showComposer && notice && notice.kind !== 'closed' && (
        <div className="px-3 md:px-4 pb-3">
          <div
            className={notice.kind === 'danger' ? NOTE_DANGER : NOTE_INFO}
            role={notice.kind === 'danger' ? 'alert' : 'status'}
            data-testid="qe-notice"
          >
            <p>{notice.text}</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default ThreadPanel;
